import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ChevronDownIcon,
  Collapsible,
  Loader,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useEffect, useState } from "react";
import { useMigrateLiquidityTx } from "../../../hooks/liquidity/useMigrateLiquidityTx";
import { useProfileAppContext } from "../../../stores";
import { useMigrateLiquidityContext } from "../../Dialogs/MigrateLiquidityDialog/context";
import { EMigrationTransactionBuilderState } from "../../Dialogs/MigrateLiquidityDialog/types";
import LiquidityMigrationTransactionFees from "../LiquidityMigrationTransactionFees/LiquidityMigrationTransactionFees";

const LiquidityMigrationTransactionCollapsible: FC = () => {
  const { currentLocale, t } = useI18N("fees");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const [open, setOpen] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false); // TODO: Remove this once SDK is implemented; based on tx building status
  const {
    derived: { fees, isLoadingV3Pools, selectedPools },
    flowState: { selectionChanged },
    handlers: { setBuiltTx },
    transaction: { builderState, builtTx },
  } = useMigrateLiquidityContext();
  const { handleTxBuilding } = useMigrateLiquidityTx();
  const amountPoolsMigrating = selectedPools.length;
  const isCollapsibleOpen = open && !!selectedPools.length;

  const handleCollapsibleToggle = useCallback(() => {
    if (!showLoadingAnimation) {
      setOpen((prev) => !prev);
    }
  }, [setOpen, showLoadingAnimation]);

  // Reset built tx when no pools are selected
  useEffect(() => {
    if (!selectedPools?.length) {
      setBuiltTx(undefined);
    }
  }, [selectedPools?.length]);

  // Build tx when selection changes
  useEffect(() => {
    if (selectedPools?.length && selectionChanged && !isLoadingV3Pools) {
      handleTxBuilding.mutate();
    }
  }, [selectionChanged, selectedPools?.length, isLoadingV3Pools]);

  // Show loading animation when building tx
  useEffect(() => {
    setShowLoadingAnimation(
      isLoadingV3Pools || builderState === EMigrationTransactionBuilderState.BUILD
    );
  }, [builderState, isLoadingV3Pools]);

  return (
    <Collapsible
      open={isCollapsibleOpen}
      trigger={
        <div
          className={cx(
            "motion-safe:animate-dialog-overlay-show",
            "w-full cursor-pointer rounded-lg",
            "select-none",
            "flex items-center justify-between",
            toolkitCx.layout.background.card,
            toolkitCx.layout.background.hoverable,
            toolkitCx.layout.transition.colors,
            toolkitCx.layout.spacing.vertical.sm,
            toolkitCx.layout.spacing.horizontal.md,
            {
              "rounded-bl-none rounded-br-none !bg-gray-200 will-change-auto dark:!bg-gray-700":
                isCollapsibleOpen,
            }
          )}
          onClick={handleCollapsibleToggle}
        >
          {showLoadingAnimation ? (
            <div className="flex items-center gap-6">
              <Text className="motion-safe:animate-pulse" size="sm" variant="muted" tag="span">
                {isLoadingV3Pools
                  ? t("migrateLiquidity.loading", { ns: "liquidity" })
                  : t("txOverview.calculatingTxFees", { ns: "exchange" })}
              </Text>
              <Loader size="xsmall" />
            </div>
          ) : (
            <>
              <Text tag="span" size="sm" variant="silent">
                {t("migrateLiquidity.migrating", { ns: "liquidity" })}{" "}
                <Text tag="span" size="sm" variant="silent" weight="semibold" className="inline">
                  {amountPoolsMigrating}
                </Text>{" "}
                {t("pool", { count: amountPoolsMigrating, ns: "glossary" })}{" "}
                <SparklesIcon className={cx("inline h-3 w-3", toolkitCx.layout.text.warning)} />
              </Text>

              {selectedPools.length ? (
                <div className="flex items-center gap-1">
                  <Text tag="span" size="sm" variant="silent">
                    {t("fee", { context: "total" })}:
                  </Text>
                  <div className="flex items-center gap-[2px]">
                    {!builtTx && (
                      <Text tag="span" size="sm" variant="silent">
                        ~
                      </Text>
                    )}
                    <Balance
                      size="sm"
                      locale={currentLocale}
                      variant="silent"
                      privacy={privacyEnabled}
                      showSymbol
                      value={fees?.totalFees} // TODO: replace with value from SDK
                    />
                  </div>
                  <Tooltip
                    trigger={
                      <div className="cursor-help">
                        <InformationCircleIcon
                          className={cx("h-4 w-4", toolkitCx.layout.text.silent)}
                        />
                      </div>
                    }
                    tooltipLabel="Fees explanation"
                  />

                  <ChevronDownIcon
                    className={cx(
                      "h-4 w-4",
                      {
                        "-scale-y-100": open,
                      },
                      toolkitCx.layout.text.silent,
                      toolkitCx.layout.transition.transform
                    )}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      }
    >
      <LiquidityMigrationTransactionFees />
    </Collapsible>
  );
};

export default LiquidityMigrationTransactionCollapsible;
